import { useState, useEffect, useRef } from 'react';
import { Download, Sparkles, X } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Input';
import { Slider } from '@/components/ui/Slider';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { Dropzone } from '@/components/ui/Dropzone';
import { Badge } from '@/components/ui/Badge';
import { useApp } from '@/store/app';
import { loadImage, readFileAsDataURL } from '@/lib/utils';
import { processAndSave, canvasToBlob, stripExt } from '@/lib/bulk';

type Algo = 'lanczos' | 'bicubic' | 'nearest';

/** High-quality lanczos-like upscale via two-stage canvas resample. */
function highQualityResize(
  img: HTMLImageElement,
  targetW: number,
  targetH: number,
  algo: Algo,
): HTMLCanvasElement {
  if (algo === 'nearest') {
    const c = document.createElement('canvas');
    c.width = targetW;
    c.height = targetH;
    const ctx = c.getContext('2d')!;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(img, 0, 0, targetW, targetH);
    return c;
  }

  // Multi-step resize for better quality (mimics lanczos-like)
  const steps = algo === 'lanczos' ? 3 : 2;
  let curW = img.width;
  let curH = img.height;
  let canvas: HTMLCanvasElement = document.createElement('canvas');
  canvas.width = curW;
  canvas.height = curH;
  let ctx = canvas.getContext('2d')!;
  ctx.drawImage(img, 0, 0);

  const factorW = Math.pow(targetW / img.width, 1 / steps);
  const factorH = Math.pow(targetH / img.height, 1 / steps);

  for (let i = 0; i < steps; i++) {
    const newW = i === steps - 1 ? targetW : Math.round(curW * factorW);
    const newH = i === steps - 1 ? targetH : Math.round(curH * factorH);
    const next = document.createElement('canvas');
    next.width = newW;
    next.height = newH;
    const nctx = next.getContext('2d')!;
    nctx.imageSmoothingEnabled = true;
    nctx.imageSmoothingQuality = 'high';
    nctx.drawImage(canvas, 0, 0, curW, curH, 0, 0, newW, newH);
    canvas = next;
    ctx = nctx;
    curW = newW;
    curH = newH;
  }

  return canvas;
}

export function ImageUpscale() {
  const { pendingFile, setPendingFile } = useApp();
  const [files, setFiles] = useState<File[]>([]);
  const [previewImg, setPreviewImg] = useState<HTMLImageElement | null>(null);
  const [scale, setScale] = useState(2);
  const [algo, setAlgo] = useState<Algo>('lanczos');
  const [working, setWorking] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (pendingFile && pendingFile.type.startsWith('image/')) {
      handleFiles([pendingFile]);
      setPendingFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleFiles(incoming: File[]) {
    if (!incoming.length) return;
    const next = [...files, ...incoming.filter((f) => f.type.startsWith('image/'))];
    setFiles(next);
    if (!previewImg && next[0]) {
      const url = await readFileAsDataURL(next[0]);
      setPreviewImg(await loadImage(url));
    }
  }

  function removeFile(i: number) {
    const next = files.filter((_, idx) => idx !== i);
    setFiles(next);
    if (next.length === 0) {
      setPreviewImg(null);
    } else if (i === 0) {
      readFileAsDataURL(next[0]).then(loadImage).then(setPreviewImg);
    }
  }

  // Run preview with current settings (debounced via dependency change).
  function runPreview() {
    if (!previewImg || !canvasRef.current) return;
    const targetW = Math.round(previewImg.width * scale);
    const targetH = Math.round(previewImg.height * scale);
    const result = highQualityResize(previewImg, targetW, targetH, algo);
    const c = canvasRef.current;
    c.width = result.width;
    c.height = result.height;
    c.getContext('2d')!.drawImage(result, 0, 0);
  }

  async function exportAll() {
    if (files.length === 0) return;
    setWorking(true);
    setProgress({ done: 0, total: files.length });
    try {
      await processAndSave(
        files,
        async (file) => {
          const url = await readFileAsDataURL(file);
          const im = await loadImage(url);
          const targetW = Math.round(im.width * scale);
          const targetH = Math.round(im.height * scale);
          const c = highQualityResize(im, targetW, targetH, algo);
          const blob = await canvasToBlob(c, 'image/png');
          return { blob, filename: `${stripExt(file.name)}-${scale}x.png` };
        },
        {
          zipName: `upscaled-${scale}x`,
          onProgress: (done, total) => setProgress({ done, total }),
        },
      );
    } finally {
      setWorking(false);
      setProgress({ done: 0, total: 0 });
    }
  }

  const target = previewImg
    ? { w: Math.round(previewImg.width * scale), h: Math.round(previewImg.height * scale) }
    : null;

  return (
    <div>
      <ToolHeader
        toolId="image-upscale"
        title="Image upscaler"
        description="Multi-step Lanczos-style resampling for sharper enlargements. Drop multiple files for bulk."
        badges={
          <>
            {files.length > 1 && <Badge variant="accent">{files.length} files</Badge>}
            {target && <Badge>{target.w}×{target.h}</Badge>}
          </>
        }
      />

      {files.length === 0 ? (
        <Dropzone accept="image/*" multiple onFiles={handleFiles} icon={<Sparkles size={20} />} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="lg:order-2 space-y-4">
            <Field label="Scale" hint={`${scale}× → ${target?.w}×${target?.h}`}>
              <Slider
                value={scale}
                onChange={setScale}
                min={1.25}
                max={4}
                step={0.25}
                showValue={false}
              />
            </Field>
            <Field label="Algorithm">
              <Tabs value={algo} onValueChange={(v) => setAlgo(v as Algo)}>
                <TabsList className="w-full">
                  <TabsTrigger value="lanczos" className="flex-1 text-2xs">
                    Lanczos
                  </TabsTrigger>
                  <TabsTrigger value="bicubic" className="flex-1 text-2xs">
                    Bicubic
                  </TabsTrigger>
                  <TabsTrigger value="nearest" className="flex-1 text-2xs">
                    Pixelated
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="secondary" size="sm" onClick={runPreview}>
                <Sparkles size={11} />
                Preview
              </Button>
              <Button variant="primary" size="sm" loading={working} onClick={exportAll}>
                <Download size={11} />
                {files.length > 1 ? `Export ${files.length}` : 'Export'}
              </Button>
            </div>
            {working && progress.total > 1 && (
              <div className="text-2xs text-fg-subtle">
                Processing {progress.done} / {progress.total}…
              </div>
            )}
            <Button
              size="sm"
              variant="ghost"
              className="w-full"
              onClick={() => {
                setFiles([]);
                setPreviewImg(null);
              }}
            >
              Clear
            </Button>
            <div className="text-2xs text-fg-subtle leading-relaxed border-t border-border pt-2">
              For deep-learning style upscaling, you'd need an ML model — this tool ships the best non-ML approach.
            </div>
          </Card>

          <Card className="lg:col-span-2 lg:order-1 !p-0 overflow-hidden">
            <div className="px-4 py-2.5 border-b border-border text-xs font-medium text-fg-muted">
              Preview {files.length > 1 && <span className="text-fg-subtle">· first of {files.length}</span>}
            </div>
            <div className="p-4 bg-bg-inset min-h-[360px] flex items-center justify-center overflow-auto">
              <canvas ref={canvasRef} className="max-w-full max-h-[420px] rounded shadow-card" />
            </div>
            {files.length > 1 && (
              <div className="border-t border-border px-3 py-2">
                <div className="flex flex-wrap gap-1.5">
                  {files.map((f, i) => (
                    <div
                      key={`${f.name}-${i}`}
                      className="flex items-center gap-1 pl-2 pr-1 py-0.5 text-2xs rounded-md bg-bg-inset border border-border max-w-[200px]"
                    >
                      <span className="truncate">{f.name}</span>
                      <button
                        onClick={() => removeFile(i)}
                        className="shrink-0 size-4 rounded hover:bg-surface-hover text-fg-subtle hover:text-danger flex items-center justify-center"
                      >
                        <X size={9} />
                      </button>
                    </div>
                  ))}
                  <label className="flex items-center gap-1 px-2 py-0.5 text-2xs rounded-md border border-dashed border-border hover:border-accent hover:bg-accent/5 cursor-pointer">
                    + add more
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={(e) => {
                        const list = e.target.files;
                        if (!list || !list.length) return;
                        handleFiles(Array.from(list));
                        e.target.value = '';
                      }}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

export default ImageUpscale;
